/**
 * Customer aggregate.
 *
 * MVP scope: no state machine. The `status` flag captures coarse lifecycle
 * (active vs. suspended vs. deceased) without needing transitions in this
 * release. P2.B may revisit when fraud holds become a thing.
 *
 * KYC capture (per session-recorded requirements):
 *   - `photoRef` (primary customer photo) is REQUIRED.
 *   - `idDocRef` (ID document scan) is REQUIRED.
 *   - `additionalPhotos[]` allows extra photos (handover, signage, etc.).
 *   - `verificationLevel` is either `id_only` or `physical_address`.
 *   - When `verificationLevel === "physical_address"`, both `address` and
 *     `gps` MUST be present (cross-field invariant enforced by the Zod
 *     schema below).
 *   - `address.houseNumber` is REQUIRED when an address is captured;
 *     other address fields are optional.
 *   - `gps` is OPTIONAL for id-only verification but recorded if captured.
 *
 * KYC PII (`nationalId.raw`, `idDocRef`, `photoRef`, `additionalPhotos`)
 * is encrypted at the persistence layer (column-level AES-GCM via
 * @dfp/crypto). The domain shape carries the raw refs because policy
 * decisions need them in memory during the loan-officer verification
 * flow (P1.B.3).
 */
import { z } from "zod";
import { languageTag } from "../country/country.js";
import { gpsPointSchema } from "../geo.js";
const customerStatusSchema = z.enum(["active", "suspended", "deceased", "withdrawn"]);
const verificationLevelSchema = z.enum(["id_only", "physical_address"]);
const namesSchema = z.object({
    given: z.string().min(1),
    family: z.string().min(1),
    other: z.string().min(1).optional(),
});
const doNotVisitSchema = z.object({
    reason: z.string().min(10),
    setBy: z.string().min(1),
    setAt: z.coerce.date(),
});
const physicalAddressSchema = z.object({
    houseNumber: z.string().min(1),
    street: z.string().min(1).optional(),
    area: z.string().min(1).optional(),
    city: z.string().min(1).optional(),
    district: z.string().min(1).optional(),
    region: z.string().min(1).optional(),
    notes: z.string().min(1).optional(),
});
/**
 * Boundary validator. Phone and NationalId are passed through unvalidated
 * here — they have their own port validators (see phone.ts / national-id.ts)
 * that domain code calls explicitly.
 *
 * The cross-field invariant for `physical_address` verification is
 * enforced by `.superRefine` below: address + gps must both be present.
 */
export const customerSchema = z
    .object({
    id: z.string().uuid(),
    countryId: z.string().uuid(),
    names: namesSchema,
    phone: z.object({ raw: z.string().min(1), countryId: z.string().uuid() }),
    nationalId: z.object({ raw: z.string().min(1), countryId: z.string().uuid() }),
    photoRef: z.string().min(1),
    additionalPhotos: z.array(z.string().min(1)).default([]),
    idDocRef: z.string().min(1),
    language: z.string(),
    riskScore: z.number().int().min(0).max(1000).nullable(),
    status: customerStatusSchema,
    doNotVisit: doNotVisitSchema.optional(),
    vulnerableCustomer: z.boolean(),
    verificationLevel: verificationLevelSchema,
    address: physicalAddressSchema.optional(),
    gps: gpsPointSchema.optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
})
    .superRefine((value, ctx) => {
    if (value.verificationLevel === "physical_address") {
        if (!value.address) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["address"],
                message: "address is required when verificationLevel is physical_address",
            });
        }
        if (!value.gps) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["gps"],
                message: "gps is required when verificationLevel is physical_address",
            });
        }
    }
});
export function makeCustomer(raw) {
    const parsed = customerSchema.parse(raw);
    return {
        id: parsed.id,
        countryId: parsed.countryId,
        names: {
            given: parsed.names.given,
            family: parsed.names.family,
            ...(parsed.names.other !== undefined ? { other: parsed.names.other } : {}),
        },
        phone: { raw: parsed.phone.raw, countryId: parsed.phone.countryId },
        nationalId: {
            raw: parsed.nationalId.raw,
            countryId: parsed.nationalId.countryId,
        },
        photoRef: parsed.photoRef,
        additionalPhotos: parsed.additionalPhotos,
        idDocRef: parsed.idDocRef,
        language: languageTag(parsed.language),
        riskScore: parsed.riskScore,
        status: parsed.status,
        ...(parsed.doNotVisit !== undefined ? { doNotVisit: parsed.doNotVisit } : {}),
        vulnerableCustomer: parsed.vulnerableCustomer,
        verificationLevel: parsed.verificationLevel,
        ...(parsed.address !== undefined
            ? { address: stripUndefined(parsed.address) }
            : {}),
        ...(parsed.gps !== undefined ? { gps: parsed.gps } : {}),
        createdAt: parsed.createdAt,
        updatedAt: parsed.updatedAt,
    };
}
/**
 * Zod's `.optional()` leaves the parsed key as `undefined`, which conflicts
 * with `exactOptionalPropertyTypes`. We drop undefined entries so the spread
 * yields the expected "field absent" shape.
 */
function stripUndefined(obj) {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
        if (v !== undefined)
            out[k] = v;
    }
    return out;
}
//# sourceMappingURL=customer.js.map